import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Maximum BMI (kg/m² scale) allowed for the primary insured to proceed. */
export const MAX_BMI = 37.5;
export const KG_TO_LB = 2.20462;

/**
 * BMI via the standard imperial formula: 703 * weight(lb) / height(in)^2.
 * Returns null until both weight and height are filled in.
 */
export function computeBmi(
  weightLb: unknown,
  heightFeet: unknown,
  heightInches: unknown,
): number | null {
  const lb = Number(weightLb);
  if (!lb || lb <= 0) return null;

  const feet = Number(heightFeet ?? 0);
  const inches = Number(heightInches ?? 0);
  const totalInches = feet * 12 + inches;
  if (!totalInches || totalInches <= 0) return null;

  return (703 * lb) / (totalInches * totalInches);
}

export interface BmiValidatorOptions {
  /** Control holding the weight, always in pounds. */
  weightLbControl?: string;
  heightFeetControl?: string;
  heightInchesControl?: string;
  /** When it returns false the validator is skipped (e.g. the form is editing a person the rule does not apply to). */
  enabled?: () => boolean;
}

/** Group validator: sets `bmiExceeded` when the person's BMI is above `MAX_BMI`. */
export function bmiValidator(options: BmiValidatorOptions = {}): ValidatorFn {
  const {
    weightLbControl = 'weightLb',
    heightFeetControl = 'heightFeet',
    heightInchesControl = 'heightInches',
    enabled,
  } = options;
  return (group: AbstractControl): ValidationErrors | null => {
    if (enabled && !enabled()) return null;
    const bmi = computeBmi(
      group.get(weightLbControl)?.value,
      group.get(heightFeetControl)?.value,
      group.get(heightInchesControl)?.value,
    );
    return bmi != null && bmi > MAX_BMI ? { bmiExceeded: true } : null;
  };
}
